'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Image from 'next/image';
import { useRouter, Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { IoBedOutline, IoPeopleOutline } from 'react-icons/io5';
import { MiniCalendar } from '@/components/public/mini-calendar';
import { useBookingStore } from '@/stores/booking-store';
import type { ListRoom } from '@/types/rooms';
import type { DateInterval, CalendarDay } from '@/types/calendar';
import { computeIntervalPrice, formatShortDate } from '@/lib/calendar-utils';
import type { DesignVariant } from './design-variants';
import { formatBedInfo, formatOccupancy, InfoPill, usePriceForRoom } from './mobile-room-card';

export function DesktopRoomCard({ room, variant, ssrPrice, ssrCurrency, calendarDays, calendarYear, calendarMonth, onMonthChange, isLoadingCalendar, filterInterval, filterIntervalVersion, isDateUnavailable, onSetFilterDates }: {
  room: ListRoom;
  variant: DesignVariant;
  ssrPrice?: number | null;
  ssrCurrency?: string;
  calendarDays?: CalendarDay[];
  calendarYear: number;
  calendarMonth: number;
  onMonthChange: (year: number, month: number) => void;
  isLoadingCalendar: boolean;
  filterInterval?: DateInterval;
  filterIntervalVersion?: number;
  isDateUnavailable?: boolean;
  onSetFilterDates?: (interval: DateInterval) => void;
}) {
  const t = useTranslations('rooms');
  const router = useRouter();
  const { items, setDates, addRoom, toggleRoom, setSingleRoomCheckout } = useBookingStore();
  const isSelected = items.some((i) => i.roomSlug === room.slug);
  const hasSelection = items.length > 0;
  const bedInfo = formatBedInfo(room);
  const occupancy = formatOccupancy(room, t);
  const isNoBooking = room.status === 'visible_no_booking';
  const { price, currency, handlePriceLoaded } = usePriceForRoom(ssrPrice, ssrCurrency);

  // Date selection state — initialized from filter interval (SSR) to avoid flash
  const [interval, setInterval] = useState<DateInterval>(
    filterInterval?.checkIn && filterInterval?.checkOut ? filterInterval : { checkIn: null, checkOut: null }
  );
  const [allCalendarDays, setAllCalendarDays] = useState<CalendarDay[]>(calendarDays || []);

  // Sync from filter calendar when it changes
  useEffect(() => {
    if (filterInterval) {
      setInterval(filterInterval);
    }
  }, [filterIntervalVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIntervalChange = useCallback((newInterval: DateInterval) => {
    setInterval(newInterval);
  }, []);

  const handleCalendarDataChange = useCallback((days: CalendarDay[]) => {
    setAllCalendarDays(days);
  }, []);

  const handleClearSelection = useCallback(() => {
    setInterval({ checkIn: null, checkOut: null });
  }, []);

  const hasCompleteInterval = interval.checkIn && interval.checkOut;

  // "Book Now" pressed without dates → highlight the mini-calendar and prompt
  // the user to pick dates from it. Auto-clears once they start selecting or
  // after a short timeout so the card returns to its calm state.
  const [needsDates, setNeedsDates] = useState(false);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!needsDates) return;
    if (interval.checkIn || interval.checkOut) {
      setNeedsDates(false);
      return;
    }
    const id = setTimeout(() => setNeedsDates(false), 3500);
    return () => clearTimeout(id);
  }, [needsDates, interval.checkIn, interval.checkOut]);

  const requestDates = useCallback(() => {
    setNeedsDates(true);
    calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, []);

  // Calculate total price for selected interval
  const intervalPrice = useMemo(() => {
    if (!interval.checkIn || !interval.checkOut) return null;
    return computeIntervalPrice(interval.checkIn, interval.checkOut, allCalendarDays);
  }, [interval, allCalendarDays]);

  const currencySymbol = currency === 'EUR' ? '\u20AC' : currency;
  const desktopDateParams = filterInterval?.checkIn && filterInterval?.checkOut
    ? `?checkIn=${filterInterval.checkIn}&checkOut=${filterInterval.checkOut}` : '';

  return (
    <Link
      href={`/rooms/${room.slug}${desktopDateParams}`}
      className="block rounded-xl overflow-hidden relative group transition-shadow duration-500 aspect-[4/3]"
      style={{
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Background image */}
      {room.main_image_url ? (
        <Image
          src={room.main_image_url}
          alt={room.name}
          fill
          className={`object-cover group-hover:scale-105 transition-transform duration-[1.5s] ${(isNoBooking || isDateUnavailable) ? 'opacity-50 grayscale-[40%]' : ''}`}
          sizes="(max-width: 1280px) 50vw, 700px"
        />
      ) : (
        <div className={`absolute inset-0 bg-gray-800 ${(isNoBooking || isDateUnavailable) ? 'opacity-50 grayscale-[40%]' : ''}`} />
      )}

      {/* Gradient overlay */}
      <div
        className="absolute inset-0 z-[1]"
        style={{ background: variant.desktopOverlayGradient }}
      />

      {/* Not available badge */}
      {isNoBooking && (
        <div className="absolute top-3 right-3 z-[3] bg-black/60 text-white text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm">
          {t('notAvailable')}
        </div>
      )}
      {isDateUnavailable && !isNoBooking && (
        <div className="absolute top-0 left-0 right-0 z-[3] bg-black/70 text-white text-sm font-medium text-center py-2 backdrop-blur-sm">
          {t('notAvailableForDates')}
        </div>
      )}

      {/* Content over the image */}
      <div className={`absolute inset-0 z-[2] flex flex-col justify-between p-5 xl:p-6 ${(isDateUnavailable && !isNoBooking) ? 'pt-11 xl:pt-12 min-[1400px]:pt-10' : ''}`}>
        {/* Top section: Title */}
        <div>
          <h3
            className="font-poppins text-white text-3xl xl:text-[38px] font-semibold leading-tight backdrop-blur-[1px] w-fit"
            style={{ textShadow: variant.desktopTitleShadow }}
          >
            {room.name}
          </h3>
          <div className="flex items-center gap-3 mt-1.5">
            <span
              className="text-white text-base font-medium leading-none backdrop-blur-[1px]"
              style={{ textShadow: '1px 1px 4px black' }}
            >
              {room.room_type_name || t('room')} / {t('roomNumber', { number: room.number })}
            </span>
            {/* Actions pill */}
            <div
              className="flex items-center rounded-2xl border border-white/30 overflow-hidden h-8"
              style={{ backgroundColor: 'rgba(113, 113, 113, 0.45)', backdropFilter: 'blur(7px)' }}
            >
              <span
                className="text-white text-sm font-medium px-3 py-1.5 flex items-center gap-1"
                style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
              >
                {t('viewRoom')} &#8250;
              </span>
            </div>
          </div>
        </div>

        {/* Bottom section: Price + Info + Calendar */}
        <div className="flex justify-between items-end gap-4">
          {/* Left: Price + Room facts + Booking */}
          <div className="flex flex-col gap-3">
            {/* Room facts — stacked vertically */}
            <div className="flex flex-col gap-1 text-white/90 text-sm">
              {bedInfo && (
                <InfoPill
                  icon={<IoBedOutline className="w-4 h-4 text-white/80" />}
                  text={bedInfo}
                  className="[text-shadow:1px_1px_4px_rgba(0,0,0,0.7)] [backdrop-filter:blur(1px)]"
                />
              )}
              {occupancy && (
                <InfoPill
                  icon={<IoPeopleOutline className="w-4 h-4 text-white/80" />}
                  text={occupancy}
                  className="[text-shadow:1px_1px_4px_rgba(0,0,0,0.7)] [backdrop-filter:blur(1px)]"
                />
              )}
            </div>

            {/* Price / Booking summary + CTA */}
            {(isNoBooking || isDateUnavailable) ? (
              <span
                className={`inline-flex self-start px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 ${variant.desktopCtaBg} ${variant.desktopCtaText} ${variant.desktopCtaHover} ${variant.desktopCtaBorder}`}
                style={{ boxShadow: '0px 0px 17px -1px rgba(255,255,255,0.4)' }}
              >
                {t('viewRoom')}
              </span>
            ) : hasCompleteInterval && intervalPrice?.hasAllPrices ? (
              /* Summary + buttons glued as one continuous block.
                 Gated on hasAllPrices (C6): a stay crossing into a not-yet-
                 loaded month would otherwise show a too-low total. When prices
                 are incomplete we fall through to the buttons-only block. */
              <div className="self-stretch" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                {/* Summary panel — no bottom radius */}
                <div
                  className="flex flex-col gap-1.5 p-3 rounded-t-xl border border-b-0 border-white/30"
                  style={{ backgroundColor: 'rgba(113,113,113,0.45)', backdropFilter: 'blur(7px)', textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
                >
                  <div className="flex items-center justify-between text-white text-sm">
                    <span>{formatShortDate(interval.checkIn!)} → {formatShortDate(interval.checkOut!)}</span>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClearSelection(); }}
                      className="ml-2 text-white/60 hover:text-white transition-colors cursor-pointer"
                      aria-label={t('clearSelection')}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-white/70 text-xs">{t('nights', { count: intervalPrice.nights })}</span>
                    <span className="text-white text-2xl font-bold">{currencySymbol}{Math.round(intervalPrice.totalPrice)}</span>
                  </div>
                </div>
                {/* Button group — enclosed with side + bottom borders to match summary panel */}
                <div className="flex border border-white/30 rounded-b-xl overflow-hidden">
                  <button
                    onClick={() => {
                      if (!hasSelection && interval.checkIn && interval.checkOut) {
                        setDates(interval.checkIn, interval.checkOut);
                        onSetFilterDates?.({ checkIn: interval.checkIn, checkOut: interval.checkOut });
                      }
                      setSingleRoomCheckout(false);
                      toggleRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                    }}
                    className={`flex-1 py-2.5 px-4 text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-300 cursor-pointer border-r border-white/30 rounded-bl-xl ${
                      isSelected
                        ? 'bg-white/15 backdrop-blur-sm text-white'
                        : 'bg-transparent backdrop-blur-sm text-white hover:bg-white/10'
                    }`}
                    style={{ backgroundColor: isSelected ? undefined : 'rgba(113,113,113,0.45)', backdropFilter: 'blur(7px)' }}
                  >
                    {isSelected ? `✓ ${t('selected')}` : t('select')}
                  </button>
                  <button
                    onClick={() => {
                      if (hasSelection) {
                        addRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                      } else {
                        setSingleRoomCheckout(true);
                        setDates(interval.checkIn!, interval.checkOut!);
                        addRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                      }
                      router.push('/checkout');
                    }}
                    className="flex-1 py-2.5 px-4 text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-300 bg-[#801515] text-white hover:bg-[#601010] cursor-pointer rounded-br-xl"
                  >
                    {t('bookNow')}{hasSelection ? ` (${items.length + (isSelected ? 0 : 1)})` : ''}
                  </button>
                </div>
              </div>
            ) : hasCompleteInterval ? (
              /* Buttons only (no price data yet) */
              <div className="flex self-stretch rounded-lg border border-white/30 overflow-hidden" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <button
                  onClick={() => {
                    if (!hasSelection && interval.checkIn && interval.checkOut) {
                      setDates(interval.checkIn, interval.checkOut);
                      onSetFilterDates?.({ checkIn: interval.checkIn, checkOut: interval.checkOut });
                    }
                    setSingleRoomCheckout(false);
                    toggleRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                  }}
                  className={`flex-1 py-2.5 px-4 text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-300 cursor-pointer border-r border-white/30 rounded-l-lg ${
                    isSelected
                      ? 'bg-white/15 backdrop-blur-sm text-white'
                      : 'bg-transparent backdrop-blur-sm text-white hover:bg-white/10'
                  }`}
                  style={{ backgroundColor: isSelected ? undefined : 'rgba(113,113,113,0.45)', backdropFilter: 'blur(7px)' }}
                >
                  {isSelected ? `✓ ${t('selected')}` : t('select')}
                </button>
                <button
                  onClick={() => {
                    if (hasSelection) {
                      addRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                    } else {
                      setSingleRoomCheckout(true);
                      setDates(interval.checkIn!, interval.checkOut!);
                      addRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                    }
                    router.push('/checkout');
                  }}
                  className="flex-1 py-2.5 px-4 text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-300 bg-[#801515] text-white hover:bg-[#601010] cursor-pointer rounded-r-lg"
                >
                  {t('bookNow')}{hasSelection ? ` (${items.length + (isSelected ? 0 : 1)})` : ''}
                </button>
              </div>
            ) : price !== null ? (
              <div className="flex flex-col items-start gap-2.5">
                <span
                  className="text-white text-sm font-normal"
                  style={{ textShadow: '1px 1px 4px black' }}
                >
                  {t('startingFrom')}
                </span>
                <span
                  className="text-white text-[46px] font-bold leading-none"
                  style={{ textShadow: '1px 1px 12px rgba(0,0,0,0.65)' }}
                >
                  {`${currencySymbol}${price}`}
                </span>
                <span
                  className="text-white text-sm font-normal"
                  style={{ textShadow: '1px 1px 4px black' }}
                >
                  {t('perNight')}
                </span>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); requestDates(); }}
                  className="inline-flex self-start px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 bg-[#801515] text-white hover:bg-[#601010] cursor-pointer"
                  style={{ boxShadow: '0px 0px 17px -1px rgba(128,21,21,0.4)' }}
                >
                  {t('bookNow')}
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); requestDates(); }}
                className="inline-flex self-start px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 bg-[#801515] text-white hover:bg-[#601010] cursor-pointer"
                style={{ boxShadow: '0px 0px 17px -1px rgba(128,21,21,0.4)' }}
              >
                {t('bookNow')}
              </button>
            )}
          </div>

          {/* Right: Calendar (stops link navigation) */}
          {!isNoBooking && (
            <div
              ref={calendarRef}
              className={`mini-calendar-area shrink-0 relative rounded-xl transition-all duration-500 ${
                needsDates
                  ? 'ring-2 ring-white/85 ring-offset-2 ring-offset-transparent shadow-[0_0_22px_rgba(255,255,255,0.45)]'
                  : ''
              }`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {needsDates && (
                <div
                  className="absolute -top-7 right-0 text-white text-xs font-medium whitespace-nowrap animate-fade-in pointer-events-none"
                  style={{ textShadow: '1px 1px 4px black' }}
                >
                  {t('selectDatesPrompt')} &#8595;
                </div>
              )}
              <MiniCalendar
                roomSlug={room.slug}
                interval={interval}
                onIntervalChange={handleIntervalChange}
                onPriceLoaded={handlePriceLoaded}
                onCalendarDataChange={handleCalendarDataChange}
                initialDays={calendarDays}
                initialCurrency={ssrCurrency}
                initialMinPrice={ssrPrice ?? null}
                controlledYear={calendarYear}
                controlledMonth={calendarMonth}
                onMonthChange={onMonthChange}
                isParentLoading={isLoadingCalendar}
              />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
